/**
 * Saved Searches functionality for Cycle Exchange
 * Handles saving, retrieving, and deleting saved searches
 * Works on both collection pages and search results pages
 */

const SavedSearches = {
  apiUrl: null,
  customerEmail: null,
  customerLoggedIn: false,
  collectionTitle: '',
  isSearchPage: false,
  searchQuery: '',

  init(config) {
    this.apiUrl = config.apiUrl;
    this.customerEmail = config.customerEmail;
    this.customerLoggedIn = config.customerLoggedIn;
    this.collectionTitle = config.collectionTitle || '';

    // Check if we're on a search results page
    this.isSearchPage = window.location.pathname.includes('search-results-page') ||
                        window.location.pathname === '/search';

    // Extract search query if on search page
    const params = new URLSearchParams(window.location.search);
    this.searchQuery = params.get('q') || '';

    // Attach click handler to button (for collection pages)
    this.attachButtonHandler();

    // If on search page, inject button when Searchanise loads
    if (this.isSearchPage) {
      this.initSearchPageButton();
    }
  },

  // Initialize button for search results page
  initSearchPageButton() {
    // Wait for Searchanise to load and inject button
    const injectButton = () => {
      // Look for Searchanise results header
      const resultsHeader = document.querySelector('#snize_results_header, .snize-results-header, #snize-search-results-header');

      // Also check for any existing title area in search results
      const searchTitle = document.querySelector('.snize-search-title, #snize_results .snize-title');

      // Find a good place to inject
      let targetElement = resultsHeader || searchTitle;

      // If no Searchanise element yet, try after a delay
      if (!targetElement) {
        const pageHeader = document.querySelector('.page-header, .template-page h1, main h1');
        if (pageHeader) {
          targetElement = pageHeader;
        }
      }

      // Don't inject if button already exists
      if (document.querySelector('[data-save-search]')) return;

      if (targetElement) {
        const btn = document.createElement('a');
        btn.href = '#';
        btn.className = 'save-search-btn';
        btn.setAttribute('data-save-search', '');
        btn.setAttribute('data-no-instant', 'true');
        btn.textContent = 'Save Your Search';

        // Insert after the target element
        targetElement.parentNode.insertBefore(btn, targetElement.nextSibling);

        // Attach handler to new button
        this.attachButtonHandler();
      }
    };

    // Try immediately
    injectButton();

    // Also try when Searchanise updates results
    document.addEventListener('Searchanise.ResultsUpdated', () => {
      setTimeout(injectButton, 100);
    });

    // And when Searchanise loads
    document.addEventListener('Searchanise.Loaded', () => {
      setTimeout(injectButton, 500);
    });

    // Fallback: try again after DOM is fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(injectButton, 1000));
    } else {
      setTimeout(injectButton, 1000);
    }
  },

  // Attach click handler to save search button
  attachButtonHandler() {
    const btn = document.querySelector('[data-save-search]');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSaveClick();
      });
    }
  },

  // Handle save button click
  handleSaveClick() {
    if (!this.customerLoggedIn) {
      this.promptLogin();
      return;
    }

    const searchName = this.generateSearchName();
    this.saveSearch(searchName);
  },

  // Generate readable name from URL params (Searchanise rb_ params)
  generateSearchName() {
    const params = new URLSearchParams(window.location.search);
    const parts = [];

    console.log('[SavedSearches] generateSearchName called, isSearchPage:', this.isSearchPage, 'searchQuery:', this.searchQuery);

    // Start with search query if on search page
    if (this.isSearchPage && this.searchQuery) {
      parts.push(`"${this.searchQuery.trim()}"`);
    }
    // Or collection title if on collection page
    else if (this.collectionTitle) {
      parts.push(this.collectionTitle);
    }

    // Parse Searchanise rb_ parameters
    for (const [key, value] of params) {
      if (!value) continue;

      // Price range: rb_price=2495,3000
      if (key === 'rb_price') {
        const [min, max] = value.split(',');
        if (min && max) {
          parts.push(`£${parseInt(min).toLocaleString()}-£${parseInt(max).toLocaleString()}`);
        } else if (min) {
          parts.push(`From £${parseInt(min).toLocaleString()}`);
        }
        continue;
      }

      // Product type: rb_product_type=Gravel/CX Bike
      if (key === 'rb_product_type') {
        const cleanValue = decodeURIComponent(value).replace(/\//g, '/');
        if (!parts.includes(cleanValue) && cleanValue !== this.collectionTitle) {
          parts.push(cleanValue);
        }
        continue;
      }

      // Size: rb_snize_facet1=XS (47cm - 50cm)
      if (key === 'rb_snize_facet1' || key.includes('size')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Brand: rb_snize_facet2 or similar
      if (key === 'rb_snize_facet2' || key.includes('brand')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Material: rb_snize_facet3 or similar
      if (key === 'rb_snize_facet3' || key.includes('material')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Groupset: rb_snize_facet4 or similar
      if (key === 'rb_snize_facet4' || key.includes('groupset')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Generic rb_ params we haven't specifically handled
      if (key.startsWith('rb_') && key !== 'rb_page' && !key.includes('sort')) {
        const cleanValue = decodeURIComponent(value)
          .replace(/%20/g, ' ')
          .replace(/\+/g, ' ');
        if (cleanValue && !parts.includes(cleanValue)) {
          parts.push(cleanValue);
        }
      }
    }

    // If we have parts, join them; otherwise use fallback
    console.log('[SavedSearches] parts:', parts);
    if (parts.length > 0) {
      // Limit length - take first 5 parts max
      const name = parts.slice(0, 5).join(', ');
      console.log('[SavedSearches] Generated name:', name);
      return name;
    }

    // Fallback if no parts at all
    console.log('[SavedSearches] Using fallback name');
    return this.collectionTitle || 'Saved Search';
  },

  // Save search to Airtable via worker
  async saveSearch(searchName) {
    if (!this.apiUrl) {
      console.error('SavedSearches: No API URL configured');
      return;
    }

    // Show saving state on button
    const btn = document.querySelector('[data-save-search]');
    const originalText = btn?.innerHTML;
    if (btn) {
      btn.innerHTML = '<span class="save-search-btn__spinner"></span> Saving...';
      btn.disabled = true;
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save',
          email: this.customerEmail,
          search: {
            name: searchName,
            url: window.location.href,
            collection: window.location.pathname,
          }
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        this.showToast('Search saved! View it in your account.', 'success');
        // Update button to show saved state
        if (btn) {
          btn.innerHTML = 'Search Saved';
          btn.classList.add('save-search-btn--saved');
        }
      } else if (data.duplicate) {
        this.showToast('You\'ve already saved this search.', 'info');
        if (btn) {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }
      } else {
        throw new Error(data.error || 'Failed to save');
      }

    } catch (error) {
      console.error('SavedSearches: Error saving', error);
      this.showToast('Unable to save search. Please try again.', 'error');
      if (btn) {
        btn.innerHTML = originalText;
        btn.disabled = false;
      }
    }
  },

  // Redirect to login with return URL
  promptLogin() {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/account/login?return_url=${returnUrl}`;
  },

  // Show toast notification
  showToast(message, type = 'success') {
    // Remove existing toast
    const existing = document.querySelector('.save-search-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `save-search-toast save-search-toast--${type}`;
    toast.innerHTML = `
      <span>${message}</span>
      ${type === 'success' ? '<a href="/account#saved" class="save-search-toast__link">View Saved Searches</a>' : ''}
    `;
    document.body.appendChild(toast);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.classList.add('save-search-toast--hide');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  },

  // For account page: Get saved searches
  async getSavedSearches() {
    if (!this.apiUrl || !this.customerEmail) return { searches: [] };

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get',
          email: this.customerEmail
        })
      });

      if (!response.ok) throw new Error('Failed to fetch');
      return response.json();

    } catch (error) {
      console.error('SavedSearches: Error fetching', error);
      return { searches: [], error: true };
    }
  },

  // For account page: Delete saved search
  async deleteSearch(searchId) {
    if (!this.apiUrl || !this.customerEmail) return false;

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          email: this.customerEmail,
          searchId: searchId
        })
      });

      return response.ok;

    } catch (error) {
      console.error('SavedSearches: Error deleting', error);
      return false;
    }
  }
};

// Export for use in other scripts
window.SavedSearches = SavedSearches;
